import { Drawer } from '@/components/shared/Drawer'
import { assetPath } from '@/utils/assetPath'
import { Button } from '@/components/shared/atoms/Button'
import { useRef, type ChangeEvent } from 'react'

interface ChooseFileBottomSheetProps {
    open: boolean
    onClose: () => void
    // called when a file is chosen via the hidden inputs
    onFileChange: (e: ChangeEvent<HTMLInputElement>) => void
    // optional handler to remove the existing image
    onRemove?: () => void
    showRemoveButton?: boolean
}

export function ChooseFileBottomSheet({
    open,
    onClose,
    onFileChange,
    onRemove,
    showRemoveButton = false,
}: Readonly<ChooseFileBottomSheetProps>) {
    const libraryInputRef = useRef<HTMLInputElement>(null)
    const cameraInputRef = useRef<HTMLInputElement>(null)

    return (
        <Drawer
            triggerContent={null}
            open={open}
            onOpenChange={onClose}
            overlayClassName="!z-[1000]"
            contentClassName="!z-[1001]"
        >
            <div>
                {/* hidden file pickers for library and camera */}
                <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    hidden
                    ref={libraryInputRef}
                    onChange={onFileChange}
                />
                <input
                    type="file"
                    accept="image/*,.heic,.heif"
                    capture="environment"
                    hidden
                    ref={cameraInputRef}
                    onChange={onFileChange}
                />
                <div className="space-y-2 pb-safe">
                    <button
                        onClick={() => libraryInputRef.current?.click()}
                        className="flex w-full items-center gap-2 rounded-2xl border border-[#f0f0f0] p-4 text-sm font-medium text-black"
                    >
                        <img
                            alt="icon"
                            width={24}
                            height={24}
                            src={assetPath('icons/folder.svg')}
                        />
                        <span>Choose from library</span>
                    </button>

                    <button
                        onClick={() => cameraInputRef.current?.click()}
                        className="flex w-full items-center gap-2 rounded-2xl border border-[#f0f0f0] p-4 text-sm font-medium text-black md:hidden"
                    >
                        <img
                            alt="icon"
                            width={24}
                            height={24}
                            src={assetPath('icons/camera.svg')}
                        />
                        <span>Take photo</span>
                    </button>

                    {showRemoveButton && (
                        <button
                            onClick={() => {
                                onRemove?.()
                                onClose()
                            }}
                            className="flex w-full items-center gap-2 rounded-2xl border border-[#f0f0f0] p-4 text-sm font-medium text-[#D91818]"
                        >
                            <img
                                alt="icon"
                                width={24}
                                height={24}
                                src={assetPath('icons/trash-red.svg')}
                            />
                            <span>Remove photo</span>
                        </button>
                    )}

                    <Button
                        onClick={onClose}
                        fullWidth
                        variant="outline"
                        className="mt-4 border-[1.5px] py-3.5"
                    >
                        Close
                    </Button>
                </div>
            </div>
        </Drawer>
    )
}
